import config, { isDev } from "src/config";
import { FORCE_PRIMARY_API } from "../featureFlags";
import useMultiRegionStore from "./middleware/multiRegionMiddleware/stores/useMultiRegionStore";

const getHeaders = (headers: HeadersInit | undefined) => {
	if (!headers) return null;
	return new Headers(headers);
};
type GetGraphQLApiUrl = (args?: {
	/**
	 * Number of attempts previously made to connect to the API.
	 * If this is greater than 0, a fallback endpoint will likely be used.
	 */
	numAttempts?: number;
	headers?: HeadersInit;
	isServer?: boolean;
}) => string;

const isIntegrationTest = process.env.VITEST === "1";

const getGraphQLApiUrlInternal: GetGraphQLApiUrl = ({
	numAttempts = 0,
	headers,
	isServer = false,
} = {}) => {
	const xPinBehavior = getHeaders(headers)?.get("x-pin-behavior");
	if (isServer) {
		// Server side
		if (xPinBehavior === "inert") {
			if (numAttempts > 0) {
				return config.graphqlConfig.fallback_endpoint;
			}
			return config.graphqlConfig.api_endpoint;
		} else {
			if (numAttempts > 0) {
				// Alternating between primary region fallback and read fallback (which is often in another region)
				// Read failures are likely due to a region's database being under heavy read load
				if (numAttempts % 2 === 1) {
					return config.graphqlConfig.read_fallback_endpoint;
				} else {
					return config.graphqlConfig.fallback_endpoint;
				}
			}
			return config.graphqlConfig.read_endpoint;
		}
	} else {
		// Client side
		if (xPinBehavior === "long") {
			if (numAttempts > 0) {
				return config.graphqlConfig.fallback_endpoint;
			}
			return config.graphqlConfig.long_endpoint;
		} else if (useMultiRegionStore.getState().checkIsPinned() || xPinBehavior === "inert") {
			if (numAttempts > 0) {
				return config.graphqlConfig.fallback_endpoint;
			}
			return config.graphqlConfig.api_endpoint;
		} else {
			if (numAttempts > 0) {
				// Alternating between primary region fallback and read fallback (which is often in another region)
				// Read failures are likely due to a region's database being under heavy read load
				if (numAttempts % 2 === 1) {
					return config.graphqlConfig.read_fallback_endpoint;
				} else {
					return config.graphqlConfig.fallback_endpoint;
				}
			}
			return config.graphqlConfig.read_endpoint;
		}
	}
};

const getGraphQLApiUrl: GetGraphQLApiUrl = isIntegrationTest
	? () => {
			// Tests just use the primary endpoint
			return config.graphqlConfig.api_endpoint;
		}
	: isDev
		? (args) => {
				if (FORCE_PRIMARY_API) return config.graphqlConfig.api_endpoint;
				if (process.env.GRAPHQL_API_URL) return process.env.GRAPHQL_API_URL;
				return getGraphQLApiUrlInternal(args);
			}
		: getGraphQLApiUrlInternal;

export default getGraphQLApiUrl;
